import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Pencil, Sparkles, Save, X, Check, TrendingUp, DollarSign, Wallet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { platformApi } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money4 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
const num = (n) => Number(n || 0).toLocaleString();
const inputCls = "h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 w-full";
const labelCls = "block text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5";

// Economics helpers (shared with PackageModal)
function pkgEconomics(pkg, config) {
  const total = pkg.totalCredits != null ? Number(pkg.totalCredits) : Number(pkg.credits || 0) + Number(pkg.bonusCredits || 0);
  const price = Number(pkg.priceUsd || 0);
  const cost = total * Number(config.costPerCredit || 0);
  const fee = price * (Number(config.stripePct || 0) / 100) + Number(config.stripeFixed || 0);
  const profit = price - cost - fee;
  const marginPct = price > 0 ? (profit / price) * 100 : 0;
  // Suggested price to hit the target margin AFTER Stripe fees.
  const denom = 1 - Number(config.stripePct || 0) / 100 - Number(config.marginPct || 0) / 100;
  const suggested = denom > 0 ? (cost + Number(config.stripeFixed || 0)) / denom : 0;
  return { total, price, cost, fee, profit, marginPct, suggested };
}

export default function CreditsAdmin() {
  const [config, setConfig] = useState(null);
  const [packages, setPackages] = useState([]);
  const [margin, setMargin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [editPkg, setEditPkg] = useState(null);

  const refreshMargin = () => platformApi.creditMargin().then(setMargin).catch(() => {});

  const load = async () => {
    try {
      const [cfg, pkgs] = await Promise.all([platformApi.getCreditConfig(), platformApi.listCreditPackages()]);
      setConfig(cfg);
      setPackages(pkgs || []);
      refreshMargin();
    } catch (_) {
      toast.error("Could not load AI credit settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const saved = await platformApi.updateCreditConfig({
        marginPct: Number(config.marginPct),
        usdPerCredit: Number(config.usdPerCredit),
        stripePct: Number(config.stripePct),
        stripeFixed: Number(config.stripeFixed),
        lowBalanceThreshold: Number(config.lowBalanceThreshold),
        popupThreshold: Number(config.popupThreshold),
        enabled: !!config.enabled,
      });
      setConfig(saved);
      refreshMargin();
      toast.success("AI credit settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSavingCfg(false);
    }
  };

  const delPkg = async (id) => {
    if (!window.confirm("Delete this credit package? Companies will no longer be able to buy it.")) return;
    try {
      await platformApi.deleteCreditPackage(id);
      toast.success("Package deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>;

  const sample = config ? Math.round(1 / Number(config.costPerCredit || 0.007143)) : 0;

  return (
    <div className="space-y-6" data-testid="platform-credits-admin">
      {/* Economics config */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-amber-400" /><h3 className="text-[15px] font-bold text-slate-100">Credit economics</h3></div>
        <p className="text-[12px] text-slate-400 mb-4">Set your <strong>target profit margin</strong> — the share of each credit's revenue you keep after paying Emergent. Usage deducts credits = (your base AI cost ÷ (1 − margin%)) ÷ $/credit. Base AI cost (what you pay Emergent) is set in the <strong>Voice AI</strong> tab.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Target profit margin %</label>
            <input data-testid="credit-cfg-margin" type="number" min="0" max="99" step="1" value={config.marginPct}
              onChange={(e) => setConfig((c) => ({ ...c, marginPct: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>$ per credit (price)</label>
            <input data-testid="credit-cfg-usd" type="number" min="0.001" step="0.001" value={config.usdPerCredit}
              onChange={(e) => setConfig((c) => ({ ...c, usdPerCredit: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Credit gating</label>
            <button data-testid="credit-cfg-enabled" onClick={() => setConfig((c) => ({ ...c, enabled: !c.enabled }))}
              className={`h-9 w-full rounded-lg border text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 ${config.enabled ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-slate-400"}`}>
              <Check className="h-4 w-4" /> {config.enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div>
            <label className={labelCls}>Stripe fee %</label>
            <input data-testid="credit-cfg-stripe-pct" type="number" min="0" step="0.1" value={config.stripePct}
              onChange={(e) => setConfig((c) => ({ ...c, stripePct: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Stripe fixed fee $</label>
            <input data-testid="credit-cfg-stripe-fixed" type="number" min="0" step="0.01" value={config.stripeFixed}
              onChange={(e) => setConfig((c) => ({ ...c, stripeFixed: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Low-balance warning (credits)</label>
            <input data-testid="credit-cfg-threshold" type="number" min="0" step="50" value={config.lowBalanceThreshold}
              onChange={(e) => setConfig((c) => ({ ...c, lowBalanceThreshold: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Buy-credits popup (credits)</label>
            <input data-testid="credit-cfg-popup-threshold" type="number" min="0" step="10" value={config.popupThreshold}
              onChange={(e) => setConfig((c) => ({ ...c, popupThreshold: e.target.value }))} className={inputCls} />
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">The buy-credits popup auto-opens for a company right after it uses any AI feature while its balance is at or below this number.</p>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <p className="text-[11px] text-slate-500">
            = <strong className="text-cyan-300">{Number(config.marginPct).toFixed(1)}% margin</strong> (a {Number(config.markupPct ?? config.profitPct).toFixed(0)}% markup). Your real cost ≈ <strong className="text-slate-300">{money4(config.costPerCredit)}/credit</strong> · sell at {money4(config.usdPerCredit)}/credit · <strong className="text-slate-300">{num(sample)} credits</strong> per $1 of AI cost.
          </p>
          <button data-testid="credit-cfg-save" onClick={saveConfig} disabled={savingCfg}
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
            {savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
          </button>
        </div>
      </div>

      {/* Profit & margin dashboard */}
      {margin && <ProfitDashboard margin={margin} />}

      {/* Packages */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
          <h3 className="text-[15px] font-bold text-slate-100">Credit packages for sale</h3>
          <button data-testid="credit-new-package" onClick={() => setEditPkg({})}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold">
            <Plus className="h-4 w-4" /> New package
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px] min-w-[720px]">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <tr>
                <th className="px-5 py-2.5 font-semibold">Name</th>
                <th className="px-3 py-2.5 font-semibold text-right">Total credits</th>
                <th className="px-3 py-2.5 font-semibold text-right">Price</th>
                <th className="px-3 py-2.5 font-semibold text-right">Your cost</th>
                <th className="px-3 py-2.5 font-semibold text-right">Profit</th>
                <th className="px-3 py-2.5 font-semibold text-right">Margin</th>
                <th className="px-3 py-2.5 font-semibold text-center">Status</th>
                <th className="px-5 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {packages.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-500">No packages yet. Create one to sell credits.</td></tr>
              ) : packages.map((p) => {
                const e = pkgEconomics(p, config);
                return (
                  <tr key={p.id} className="text-slate-200" data-testid={`credit-pkg-row-${p.id}`}>
                    <td className="px-5 py-3 font-medium">
                      {p.name}
                      {p.bonusCredits ? <span className="ml-1.5 text-[11px] text-emerald-400">+{num(p.bonusCredits)} bonus</span> : null}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold">{num(e.total)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{money(e.price)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-400">{money(e.cost)}</td>
                    <td className={`px-3 py-3 text-right tabular-nums font-semibold ${e.profit < 0 ? "text-rose-400" : "text-emerald-300"}`}>{money(e.profit)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {e.profit < 0 ? (
                        <span className="inline-flex items-center gap-1 text-rose-400 text-[12px] font-semibold" data-testid={`credit-pkg-belowcost-${p.id}`}><AlertTriangle className="h-3.5 w-3.5" /> below cost</span>
                      ) : (
                        <span className={`font-semibold ${e.marginPct < 15 ? "text-amber-300" : "text-slate-200"}`}>{e.marginPct.toFixed(1)}%</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${p.active ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-slate-500"}`}>{p.active ? "Active" : "Hidden"}</span>
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button data-testid={`credit-pkg-edit-${p.id}`} onClick={() => setEditPkg(p)} className="p-1.5 rounded-md hover:bg-white/10 text-slate-400 hover:text-slate-200"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`credit-pkg-delete-${p.id}`} onClick={() => delPkg(p.id)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="px-5 py-2.5 text-[11px] text-slate-500 border-t border-white/5">Profit = price − (total credits × your cost/credit) − Stripe fees. Bonus credits lower your margin.</p>
      </div>

      {editPkg && (
        <PackageModal pkg={editPkg} config={config} onClose={() => setEditPkg(null)} onSaved={() => { setEditPkg(null); load(); }} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-100", emerald: "text-emerald-300", cyan: "text-cyan-300", amber: "text-amber-300", rose: "text-rose-300",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className={`text-[22px] font-bold tabular-nums ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ProfitDashboard({ margin }) {
  const { sales, usage, companies } = margin;
  return (
    <div className="space-y-4" data-testid="credit-profit-dashboard">
      <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-400" /><h3 className="text-[15px] font-bold text-slate-100">Profit &amp; margin</h3></div>

      {/* Sales */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Credit sales (all-time)</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Wallet} label="Credits sold" value={num(sales.creditsSold)} sub={`${num(sales.purchaseCount)} purchases`} tone="cyan" testid="stat-credits-sold" />
          <StatCard icon={DollarSign} label="Sales revenue" value={money(sales.revenueUsd)} sub="gross" tone="emerald" testid="stat-revenue" />
          <StatCard icon={DollarSign} label="Stripe fees" value={money(sales.stripeFeesUsd)} sub="processing" tone="rose" testid="stat-stripe-fees" />
          <StatCard icon={DollarSign} label="Net revenue" value={money(sales.netRevenueUsd)} sub="after Stripe" tone="emerald" testid="stat-net-revenue" />
        </div>
      </div>

      {/* Usage / realized margin */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Realized margin on usage (all-time)</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Sparkles} label="Credits used" value={num(usage.creditsConsumed)} sub={`worth ${money(usage.consumedValueUsd)}`} tone="slate" testid="stat-credits-used" />
          <StatCard icon={DollarSign} label="Real AI cost" value={money(usage.realCostUsd)} sub="paid to Emergent" tone="amber" testid="stat-real-cost" />
          <StatCard icon={TrendingUp} label="Gross profit" value={money(usage.grossProfitUsd)} sub="value − cost" tone="emerald" testid="stat-gross-profit" />
          <StatCard icon={TrendingUp} label="Blended margin" value={`${usage.marginPct}%`} sub="realized" tone="cyan" testid="stat-blended-margin" />
        </div>
      </div>

      {/* Per company */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 text-[13px] font-semibold text-slate-200">By company</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px] min-w-[640px]">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <tr>
                <th className="px-5 py-2.5 font-semibold">Company</th>
                <th className="px-3 py-2.5 font-semibold text-right">Credits bought</th>
                <th className="px-3 py-2.5 font-semibold text-right">$ spent</th>
                <th className="px-3 py-2.5 font-semibold text-right">Credits used</th>
                <th className="px-3 py-2.5 font-semibold text-right">AI cost</th>
                <th className="px-5 py-2.5 font-semibold text-right">Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {companies.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No credit activity yet.</td></tr>
              ) : companies.map((c) => (
                <tr key={c.companyId} className="text-slate-200" data-testid={`credit-company-row-${c.companyId}`}>
                  <td className="px-5 py-3 font-medium">{c.name}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{num(c.creditsPurchased)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-300">{money(c.spentUsd)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{num(c.creditsConsumed)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-400">{money(c.realCostUsd)}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-300">{money(c.profitUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PackageModal({ pkg, config, onClose, onSaved }) {
  const isNew = !pkg.id;
  const [f, setF] = useState({
    name: pkg.name || "",
    credits: pkg.credits ?? 1000,
    bonusCredits: pkg.bonusCredits ?? 0,
    priceUsd: pkg.priceUsd ?? 10,
    sortOrder: pkg.sortOrder ?? 0,
    active: pkg.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const e = pkgEconomics({ credits: f.credits, bonusCredits: f.bonusCredits, priceUsd: f.priceUsd }, config);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        credits: Number(f.credits),
        bonusCredits: Number(f.bonusCredits),
        priceUsd: Number(f.priceUsd),
        sortOrder: Number(f.sortOrder),
        active: !!f.active,
      };
      if (isNew) await platformApi.createCreditPackage(payload);
      else await platformApi.updateCreditPackage(pkg.id, payload);
      toast.success(isNew ? "Package created." : "Package updated.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save package.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="credit-package-modal">
      <div className="w-full max-w-md rounded-2xl bg-[#0F1629] border border-white/10 shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-[15px] font-bold text-slate-100">{isNew ? "New credit package" : "Edit package"}</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Package name</label>
            <input data-testid="pkg-name" value={f.name} onChange={(ev) => setF({ ...f, name: ev.target.value })} className={inputCls} placeholder="e.g. Growth Pack" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Credits</label>
              <input data-testid="pkg-credits" type="number" min="1" value={f.credits} onChange={(ev) => setF({ ...f, credits: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Bonus credits</label>
              <input data-testid="pkg-bonus" type="number" min="0" value={f.bonusCredits} onChange={(ev) => setF({ ...f, bonusCredits: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Price (USD)</label>
              <input data-testid="pkg-price" type="number" min="0.5" step="0.01" value={f.priceUsd} onChange={(ev) => setF({ ...f, priceUsd: ev.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Sort order</label>
              <input data-testid="pkg-sort" type="number" value={f.sortOrder} onChange={(ev) => setF({ ...f, sortOrder: ev.target.value })} className={inputCls} />
            </div>
          </div>
          <button onClick={() => setF({ ...f, active: !f.active })} data-testid="pkg-active"
            className={`h-9 px-3 rounded-lg border text-[13px] font-semibold inline-flex items-center gap-1.5 ${f.active ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-slate-400"}`}>
            <Check className="h-4 w-4" /> {f.active ? "Active (visible to companies)" : "Hidden"}
          </button>

          {/* Live economics */}
          <div className={`rounded-lg border p-3 text-[12px] space-y-1 ${e.profit < 0 ? "border-rose-500/40 bg-rose-500/10" : "border-white/10 bg-white/[0.03]"}`} data-testid="pkg-economics">
            <div className="flex justify-between text-slate-400"><span>Total delivered</span><span className="text-slate-200 font-semibold">{num(e.total)} credits</span></div>
            <div className="flex justify-between text-slate-400"><span>Your cost (Emergent)</span><span className="text-slate-300">{money(e.cost)}</span></div>
            <div className="flex justify-between text-slate-400"><span>Stripe fee</span><span className="text-slate-300">{money(e.fee)}</span></div>
            <div className="flex justify-between font-semibold"><span className="text-slate-300">Profit / margin</span><span className={e.profit < 0 ? "text-rose-400" : "text-emerald-300"}>{money(e.profit)} · {e.marginPct.toFixed(1)}%</span></div>
            {e.profit < 0 ? (
              <div className="flex items-center gap-1.5 text-rose-400 pt-1"><AlertTriangle className="h-3.5 w-3.5" /> Priced below cost. Suggested ≥ <button type="button" className="underline" data-testid="pkg-use-suggested-below" onClick={() => setF({ ...f, priceUsd: Math.ceil(e.suggested * 100) / 100 })}>{money(e.suggested)}</button> for your {Number(config.marginPct).toFixed(0)}% target.</div>
            ) : (
              <div className="text-slate-500 pt-1">Suggested for {Number(config.marginPct).toFixed(0)}% margin: <button type="button" className="underline text-cyan-300" data-testid="pkg-use-suggested-above" onClick={() => setF({ ...f, priceUsd: Math.ceil(e.suggested * 100) / 100 })}>{money(e.suggested)}</button></div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 h-9 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="pkg-save"
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
